import React, { ReactNode, useEffect, useState } from 'react';

import { Grid, Hidden, makeStyles, Typography, withWidth } from '@material-ui/core';
import { WithWidth } from '@material-ui/core/withWidth';
import {
  DesktopMacOutlined,
  OpenInNewOutlined,
  PhoneIphoneOutlined,
  TabletMacOutlined,
} from '@material-ui/icons';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { myFlipdish } from '../../../helpers/externalLinks';
import { getSelectedApp } from '../../../selectors/app.selector';
import PageLayout, { OuterProps as LayoutProps } from '../../../ui/Layout';
import NavLink from '../../../ui/NavLink/NavLink';

export const LayoutContext = React.createContext<Views>('desktop');

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1000, // don't increase
    overflow: ' hidden',
    maxWidth: 'none',
    background: '#fff',

    '& > div:nth-child(2)': {
      flexGrow: 1,
      display: 'flex',
      overflow: 'hidden',
    },
  },
  titleWrapper: {
    marginLeft: 6,
    alignItems: 'flex-start',
  },
  views: {
    display: 'inline-block',
    marginRight: 10,
    marginLeft: 10,
    marginTop: -5,
    borderRight: '1px solid rgba(0, 0, 0, 0.1)',
  },
  mobileView: {
    display: 'inline-block',
    marginLeft: 10,
    marginTop: 3,
    textTransform: 'uppercase',
  },
  icon: {
    cursor: 'pointer',
    display: 'inline-flex',
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    marginRight: 6,
  },
  activeIcon: {
    backgroundColor: '#eaf2ff',
  },
}));

const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
  currentApp: getSelectedApp(state),
});

export type Views = 'desktop' | 'tablet' | 'mobile';

type OuterProps = {
  withView?: boolean;
  children: ReactNode;
} & LayoutProps & { title: TranslationId };

type Props = OuterProps & ReturnType<typeof mapStateToProps> & WithWidth;

export default compose<any, OuterProps>(
  connect(mapStateToProps),
  withWidth()
)(function LayoutWrapper(props: Props) {
  const classes = useStyles();
  const { children, translate, currentApp, width, withView, ...rest } = props;
  const [view, setView] = useState<Views>('desktop');

  useEffect(() => {
    if (width === 'xs' && view !== 'mobile') {
      setView('mobile');
    } else if (view === 'desktop' && width === 'sm') {
      setView('tablet');
    }
  }, [width, view]);

  const Title = () => {
    return (
      <Grid container className={classes.titleWrapper}>
        <Typography variant="h5" component="h2">
          {translate(props.title)}
        </Typography>
        <div>
          {!withView ? (
            <Hidden mdUp>
              <NavLink to={`/${currentApp.AppId}/website/preview`} className={classes.mobileView}>
                {translate('View')}
              </NavLink>
            </Hidden>
          ) : null}
          <>
            <Hidden smDown={!withView}>
              <Hidden xsDown={withView}>
                <div className={classes.views}>
                  <Hidden smDown>
                    <div
                      onClick={() => setView('desktop')}
                      className={`${classes.icon} ${view === 'desktop' ? classes.activeIcon : ''}`}
                    >
                      <DesktopMacOutlined />
                    </div>
                  </Hidden>
                  <Hidden xsDown>
                    <div
                      onClick={() => setView('tablet')}
                      className={`${classes.icon} ${view === 'tablet' ? classes.activeIcon : ''}`}
                    >
                      <TabletMacOutlined />
                    </div>
                  </Hidden>
                  <div
                    onClick={() => setView('mobile')}
                    className={`${classes.icon} ${view === 'mobile' ? classes.activeIcon : ''}`}
                  >
                    <PhoneIphoneOutlined />
                  </div>
                </div>
              </Hidden>
              <a
                href={myFlipdish(currentApp.panaceaVanityUrl, true)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewOutlined />
              </a>
            </Hidden>
          </>
        </div>
      </Grid>
    );
  };

  return (
    <PageLayout {...rest} className={classes.root} titleComponent={Title}>
      <LayoutContext.Provider value={view}>{children}</LayoutContext.Provider>
    </PageLayout>
  );
});
